'use client';

import { useI18n } from '@/contexts/I18nContext';
import type { WavespeedPriceQuote } from '@/hooks/useWavespeedQuote';
import { formatUsd } from '@/lib/studio/generation-inputs';

/** Original and payable prices always come from the same live quote. */
export function WavespeedPrice({ quote, quantity = 1, theme = 'dark', layout = 'stacked', className = '', id }: {
  quote: WavespeedPriceQuote;
  quantity?: number;
  theme?: 'dark' | 'light';
  layout?: 'stacked' | 'inline';
  className?: string;
  id?: string;
}) {
  const { t } = useI18n();
  const { price, base, discountRate } = quote;
  if (price === undefined || !Number.isFinite(price) || price < 0) return null;
  const payable = formatUsd(price * quantity);
  const hasOriginal = typeof base === 'number' && Number.isFinite(base) && base > price;
  // WaveSpeed's rate is the percentage PAYABLE: rate 90 means 10% off.
  const percent = hasOriginal ? Number((100 - (
    typeof discountRate === 'number' && Number.isFinite(discountRate) && discountRate >= 0 && discountRate <= 100
      ? discountRate : price / base * 100
  )).toFixed(2)) : 0;
  const discounted = percent > 0;
  const original = discounted ? formatUsd(base! * quantity) : '';
  const description = discounted
    ? t('studio.price.discountDescription', { percent, original, price: payable })
    : payable;

  return (
    <span id={id} className={`inline-flex shrink-0 whitespace-nowrap leading-tight tabular-nums ${layout === 'inline' ? 'items-center gap-1.5' : 'flex-col items-end'} ${className}`} title={description}>
      <span className="sr-only">{description}</span>
      {discounted && <del aria-hidden="true" className={theme === 'light' ? 'text-sm font-normal text-ink-500' : 'text-[10px] font-normal opacity-60'}>{original}</del>}
      <span aria-hidden="true" className="inline-flex items-center gap-1.5">
        <span>{payable}</span>
        {discounted && (
          <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${theme === 'light' ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-400/15 text-emerald-200'}`}>
            −{percent}%
          </span>
        )}
      </span>
    </span>
  );
}
